import { BuildingProperties } from "api/onboardingTypes";
import { StatusCredential } from "./auth";

export type UserProfile = {
  user: User;
  inveID?: string;
};

export type DashboardOverviewMonthlyCreditAccumulated = {
  generatedUsage: Labelpoint[];
  injectedUsage: Labelpoint[];
  instID: string;
};

export type DashboardOverviewMonthlyEnergyGenerated = {
  generatedUsage: Labelpoint[];
  injectedUsage: Labelpoint[];
  instID: string;
};

export type DashboardOverviewMonthlyEnergyUsage = {
  expectedUsage: Labelpoint[];
  instID: string;
  registeredUsage: Labelpoint[];
};

export type DashboardOverviewMonthlySavingsAccumulated = {
  generatedUsage: Labelpoint[];
  injectedUsage: Labelpoint[];
  instID: string;
};

export type DashboardOverviewValues = {
  accumulatedCredit?: number;
  instID: string;
  totalGeneratedEnergy?: number;
  totalInjectedEnergy?: number;
  totalRegisteredUsage?: number;
  ufvPerformance?: number;
  unitCount?: number;
};

export type DashboardInvestmentFinancialTransactionsStatementLine = {
  monthYear: string;
  originalBillingAmountA: number;
  originalBillingAmountB: number;
  appliedDiscountA: number;
  appliedDiscountB: number;
  clentSavingsA: number;
  clentSavingsB: number;
  billingAmountA: number;
  billingAmountB: number;
  billingAmount: number;
  finalBillingAmount: number;
  clientRefundAmount: number;
  fixedLeaseCost: number;
  fixedLeaseTaxCost: number;
  variableService: number;
  variableServiceTaxA: number;
  variableServiceTaxB: number;
  variableServiceTaxC: number;
  fixedLeaseCostTax: number;
  variableNetTax: number;
  adminitrativeCost: number;
  accountingCost: number;
  estateInsuranceCost: number;
  netAmountTax: number;
  netAmountRatio: number;
  paymentsClientRefund: number;
  pendingAprovedInvoice: number;
  ratioRate: number;
  finalGainAmount: number;
  unitEnergyInvoice: number;
};

export interface DashboardInvestmentFinancialTransactionsStatementTable {
  extractLines?: DashboardInvestmentFinancialTransactionsStatementLine[];
  instID?: string;
}

export interface DashboardInvestmentMonthlyYield {
  yieldAccumulated?: Labelpoint[];
  yieldMonthly?: Labelpoint[];
}

export interface DashboardInvestmentValues {
  totalInvestedAmountBRL?: number;
  totalInvestmentBRL?: number;
  totalInvestmentReturn?: number;
}

export type DateGraphItem = {
  time: string;
  value: number;
};

export type UnitProfile = {
  groupId: number;
  name: string;
  longitude: string;
  latitude: string;
  nominalPowerKw: string;
  peakPowerKw: string;
};

export type OperationStatus =
  | "NORMAL"
  | "NO_COMMUNICATION"
  | "UNSPECIFIED_STATUS";

type WeatherReport = {
  id: number;
  main: "Clouds" | "Sunny";
  description: string;
  icon: "string";
};

type TemperatureReport = Record<"temp" | "temp_max" | "temp_min", number>;

export type MonitoringWeatherStatus = {
  weather: Array<WeatherReport>;
  main: TemperatureReport;
};

export type MonitoringWeatherStatus_V2 = {
  weather: Array<WeatherReport>;
  condition: { temperature: number };
};

export interface DashboardMonitoringValues {
  performance: number;
  profile: UnitProfile;
  lastTimeRegistered: number;
  lastPowerRegistered: number;
  granularity: string;
  generation: Array<DateGraphItem>;
  generatedEnergy: number;
  estimatedEnergy: Array<DateGraphItem>;
  totalEstimatedEnergyKwh: number;
  statusMonitoringPlatform: StatusCredential;
  operationStatus: OperationStatus;
  weather: MonitoringWeatherStatus;
  needsEstimatedEnergy: boolean;
  monthlyEstimatedEnergyKwh: number;
  annualEstimatedEnergyKwh: number;
}

export interface DashboardMonitoringValues_V2 {
  AnnualEstimatedEnergyKwh: number;
  Consumption?: number; // tbd
  ConsumptionEnergy: number;
  ConsumptionEnergyC02E: number;
  ConsumptionEnergyMONEY: number;
  EstimatedEnergy?: Array<DateGraphItem>; // tbd
  Granularity: string;
  InjectedEnergy: number;
  InjectedEnergyC02E: number;
  InjectedEnergyMONEY: number;
  IntantaneousPower: number;
  LastPowerRegistered: number;
  LastTimeRegistered: number;
  MonthlyEstimatedEnergyKwh: number;
  NeedsEstimatedEnergy: boolean;
  NominalPower: number;
  OperationStatus: OperationStatus;
  PeakPower: number;
  Performance: number;
  Production?: Array<DateGraphItem>;
  ProductionEnergy: number;
  ProductionEnergyC02E: number;
  ProductionEnergyMONEY: number;
  Profile: UnitProfile;
  StatusMonitoringPlatform: StatusCredential;
  TotalEstimatedEnergyKwh: number;
  Weather: MonitoringWeatherStatus_V2;
}

export type MonitoringGranularity = "hourly" | "daily" | "monthly";

export type MonitoringBriefParams = {
  periodStart: string;
  periodEnd: string;
  granularity: MonitoringGranularity;
};

export type MonitoringFilters_V2 = {
  fromTime: string;
  toTime: string;
  granularity: MonitoringGranularity;
  spaceId: string;
  contractId?: string;
};

export type DashboardAdminValues = {
  institutionCount: number;
  unitCount: number;
  totalGeneratedEnergy: number;
  savingsTotal: number;
  currentDateAlertCount: number;
  totalRegisteredUsage: number;
  totalInjectedEnergy: number;
  accumulatedCredit: number;
};

export type KPIAlertNotification = {
  notiID: number;
  createdAt: string;
  recipientEmails: string[];
  kpalID: string;
  alertName: string;
  institutionName: string;
};

export type Labelpoint = {
  label?: string;
  value?: number;
};

export type User = {
  name?: string;
  email?: string;
  phone?: string;
  hasCompletedOnboarding?: boolean;
  automationSpaces?: Array<string>;
  data?: {
    item: UserData;
  };
};

export type UserData = {
  adminId: string;
  userId: string;
  email: string;
  name: string;
  createdAt: string;
  deletedAt: string;
  phone: string;
};

export type FilterDashboard = {
  instID: string;
  year: string;
  month: string;
  county?: string;
  district?: string;
  unit?: string;
  contractAccount?: string;
  periodStart: string;
  periodEnd: string;
  granularity: "hourly" | "daily" | "monthly";
};

export enum InvoiceDetailType {
  ESTIMATED_READ = "INVOICE_APPROVAL_TYPE_ESTIMATED_READ",
  CREDIT_BALANCE = "INVOICE_APPROVAL_TYPE_CREDIT_BALANCE",
  NO_INJECTED_ENERGY = "INVOICE_APPROVAL_TYPE_NO_INJECTED_ENERGY",
  COMPENSATED_ENERGY = "INVOICE_APPROVAL_TYPE_COMPENSATED_ENERGY",
  PIS_TAX = "INVOICE_APPROVAL_TYPE_PIS_TAX",
  COFINS_TAX = "INVOICE_APPROVAL_TYPE_COFINS_TAX",
  ICMS_TAX = "INVOICE_APPROVAL_TYPE_ICMS_TAX",
  COST_WITHOUT_TAXES = "INVOICE_APPROVAL_TYPE_COST_WITHOUT_TAXES",
  COST_WITH_TAXES = "INVOICE_APPROVAL_TYPE_COST_WITH_TAXES",
}

export type InvoiceDetail = {
  title: string;
  description: string;
  type: InvoiceDetailType;
};

export type InvoiceApprovalInputs = {
  calculatedCofinsTaxBrl: number;
  calculatedIcmsTaxBrl: number;
  calculatedPisTaxBrl: number;
  cofinsTaxBrl: number;
  compensatedEnergyKwh: number;
  dueDate: string;
  expectedBalanceKwh: number;
  expectedCompensatedEnergyKwh: number;
  icmsTaxBrl: number;
  injectedEnergyKwh: number;
  invoiceBalanceKwh: number;
  invoiceConsumptionKwh: number;
  minimumConsumptionKwh: number;
  pisTaxBrl: number;
  previousBalanceKwh: number;
};

export type InvoiceApprovalStatus =
  | "UNKNOWN"
  | "INCONSISTENT"
  | "CONSISTENT"
  | "NOT_AVAILABLE";

export type InvoiceApprovalProps = {
  status: InvoiceApprovalStatus;
  inconsistencies: InvoiceDetail[];
  validated: InvoiceDetail[];
  alerts: InvoiceDetail[];
  inputs: InvoiceApprovalInputs;
};

export type DashboardBillingValues = {
  savingsTotal?: number;
  finalBillingAmount?: number;
  accumulatedCredit?: number;
  registeredUsage?: number;
  compensatedEnergy?: number;
  costWithoutInjectedEnergy?: number;
  discount?: number;
  selfSufficiency?: number;
  invoiceApproval: InvoiceApprovalProps;
  statusEnergyOperator: StatusCredential;
  invoicesDownloadUrls: Array<string>;
  injectedEnergyKwh: string;
  generatedEnergyKwh: string;
  monthlyAcculumatedCreditKwh: string;
  hasGeneration: boolean;
  clientName: string;
  supplierKey: string;
  taxNumber: string;
  isProsumer: boolean;

  /** generation charge */
  totalWithoutGenerationBrl: number;
  invoiceChargeBrl: number;
  generationChargeBrl: number;
  generationEconomyBrl: number;
  generationEconomyPercentage: number;
  totalCompensationCostBrl: number;
  contractedDiscount: number;

  /** total savings */
  cosip?: number;
  cosipWithCompensation?: number;
  energyManagementCost?: number;
  fixedLeaseCost?: number;
  flagIncreaseSavings?: number;
  groupABilling?: number;
  groupADiscount?: number;
  groupATotal?: number;

  /** unit billing */
  groupBBilling?: number;
  groupBDiscount?: number;
  groupBTotal?: number;
  instID: string;

  /** final billing */
  pendingPayments?: number;
  totalCosipSavings?: number;
  totalUsageSavings?: number;
  ufvBillingAmount?: number;
};

export type DashboardFilterValue = {
  value: string;
  label: string;
};

export type ChartData = {
  time: string;
  value: number;
};

export type DashboardBillingChartsValue = {
  economy?: ChartData[];
  consumption?: ChartData[];
  credits?: ChartData[];
  generationKwh?: ChartData[];
  supplierKwh?: ChartData[];
};

export type CarbonFootprintResult = Record<
  "co2Avoided" | "kilometersNotDriven" | "treesPlanted",
  number
>;

export type InvestmentChartData = {
  time: string;
  value: string;
};

export type InvestmentsResult = {
  realProfitabilityEntries: Array<InvestmentChartData>;
  estimatedProfitabilityEntries: Array<InvestmentChartData>;
  realPaybackEntries: Array<InvestmentChartData>;
  estimatedPaybackEntries: Array<InvestmentChartData>;
  investedAmountBrl: string;
  estimatedReturnRate: number;
  estimatedPaybackTime: number;
  installationDate: string;
  unitNeedsConfiguration: boolean;
  expectedMonthlyEconomyBrl: string;
};

export type Scene = {
  id: string;
  name: string;
  isActive: boolean;
  triggerTime: string; //HH:mm
  weekmap: string; // "9999999", sun -> sat
};

export type Environment = {
  name: string;
  id: string;
  devices?: Array<Device>;
  onlineDevices?: number;
  offlineDevices?: number;
  pavementId?: string;
};

export type Device = {
  id: string;
  status: DeviceStatus;
};

export enum DeviceStatus {
  online = "DEVICE_STATUS_ONLINE",
  offline = "DEVICE_STATUS_OFFLINE",
}

export type ConsumerMonitoringData = Array<SmartMeterData>;

export type ConsumerMonitoringUnits = "CO2e" | "kWh" | "money";

export type SmartMeterData = {
  name: string;
  data: Array<ConsumerMonitoringGraph>;
  total: Record<ConsumerMonitoringUnits, number>;
};

export type ConsumerMonitoringGraph = {
  time: string;
  C02e: number;
  kWh: number;
  money: number;
};

export type AutomationSpace = {
  id: string;
  schoolName: string;
  brand: string;
  contractCode: string;
  hasDiscarbonizationProfile: boolean;
  isProsumer: boolean;
  hasSmartMeter: boolean;
  hasInfrared: boolean;
  properties?: [BuildingProperties];
};

export type DeviceAC = {
  id: string;
  environment_id: string;
  name: string;
};
